import React, { Suspense, useRef, useMemo } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF } from '@react-three/drei';
import { Box, CircularProgress, Typography } from '@mui/material';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import * as THREE from 'three';

// Error Boundary for 3D models
class ModelErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: (error: string) => void },
  { hasError: boolean; error?: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ModelErrorBoundary caught error:', error, errorInfo);
    this.props.onError?.(error.message);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 2,
          }}
        >
          <Typography variant="body2" color="error" align="center">
            Failed to load 3D model: {this.state.error}
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

// 3D Model Component with KTX2 support for Ready Player Me
function AvatarModel({ 
  url, 
  onLoad, 
  onError 
}: { 
  url: string; 
  onLoad?: () => void; 
  onError?: (error: string) => void; 
}) {
  const meshRef = useRef<THREE.Group>(null);
  const { gl } = useThree();
  
  console.log('AvatarModel: Attempting to load URL:', url);
  
  // Load GLTF with KTX2 support for Ready Player Me compressed textures
  // Note: useLoader will throw if URL is invalid, which will be caught by ErrorBoundary
  const gltf = useLoader(
    GLTFLoader,
    url,
    (loader) => {
      // Set up KTX2Loader for compressed textures (Ready Player Me uses these)
      const ktx2Loader = new KTX2Loader();
      ktx2Loader.setTranscoderPath('https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/libs/basis/');
      ktx2Loader.detectSupport(gl);
      loader.setKTX2Loader(ktx2Loader);
      console.log('AvatarModel: KTX2Loader configured');
    }
  );
  
  console.log('AvatarModel: GLTF loaded successfully:', gltf);

  // Configure materials with proper color space (THREE r160+)
  React.useEffect(() => {
    if (gltf && gltf.scene) {
      console.log('AvatarModel: Configuring materials with proper color space');
      
      // Detect if we're on mobile
      const isMobile = typeof window !== 'undefined' && (
        window.innerWidth < 768 || 
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      );
      
      // Traverse the scene and configure materials
      gltf.scene.traverse((child: any) => {
        if (child.isMesh && child.material) {
          const material = child.material;
          
          // Configure baseColor/albedo map with SRGB color space
          if (material.map) {
            material.map.colorSpace = THREE.SRGBColorSpace; // Correct for THREE r152+
            material.map.needsUpdate = true;
            console.log('AvatarModel: Set baseColor map to SRGBColorSpace');
          }
          
          // Handle additional texture maps that custom clothes might use
          // Configure emissive map (used for glowing/lit areas)
          if (material.emissiveMap) {
            material.emissiveMap.colorSpace = THREE.SRGBColorSpace;
            material.emissiveMap.needsUpdate = true;
          }
          
          // Configure alpha/opacity map (used for transparency)
          if (material.alphaMap) {
            material.alphaMap.colorSpace = THREE.NoColorSpace; // Keep linear
            material.alphaMap.needsUpdate = true;
          }
          
          // Configure ambient occlusion map (used for shadows/depth)
          if (material.aoMap) {
            material.aoMap.colorSpace = THREE.NoColorSpace; // Keep linear
            material.aoMap.needsUpdate = true;
          }
          
          // Configure light map (used for baked lighting)
          if (material.lightMap) {
            material.lightMap.colorSpace = THREE.SRGBColorSpace;
            material.lightMap.needsUpdate = true;
          }
          
          // Ensure normal/metalness/roughness maps stay LINEAR (not SRGB)
          if (material.normalMap) {
            material.normalMap.colorSpace = THREE.NoColorSpace; // Keep linear
            material.normalMap.needsUpdate = true;
          }
          if (material.metalnessMap) {
            material.metalnessMap.colorSpace = THREE.NoColorSpace; // Keep linear
            material.metalnessMap.needsUpdate = true;
          }
          if (material.roughnessMap) {
            material.roughnessMap.colorSpace = THREE.NoColorSpace; // Keep linear
            material.roughnessMap.needsUpdate = true;
          }
          
          /* 
            ⚠️ CRITICAL: Mobile Material Configuration for Custom Ready Player Me Clothes
            
            DO NOT MODIFY THESE MATERIAL SETTINGS WITHOUT TESTING ON MOBILE!
            
            Issue: Custom RPM clothes (pants and shoes especially) appear very dark on mobile.
            Standard RPM clothes work fine, but custom clothes need special material adjustments.
            
            These settings work together with the mobile lighting configuration above to ensure
            all clothing items display their correct colors on mobile devices.
            
            Verified Working: October 2025
          */
          if (isMobile) {
            // Simplify materials on mobile
            child.castShadow = false;
            child.receiveShadow = false;
            
            // For custom clothes: Keep base texture but remove complex maps
            // This allows custom clothes to display their colors/patterns
            // but reduces GPU load by removing advanced material features
            material.normalMap = null;
            material.roughnessMap = null;
            material.metalnessMap = null;
            material.aoMap = null;
            material.lightMap = null;
            
            /* CRITICAL: Material property adjustments for color visibility on mobile */
            
            // Reduce roughness to increase reflectivity - helps colors appear brighter
            // Max value of 0.7 ensures materials aren't too dull
            if (material.roughness !== undefined) {
              material.roughness = Math.min(material.roughness, 0.7);
            }
            
            // Reduce metalness - metallic materials appear too dark with simplified lighting
            // Cutting by 50% makes colors more visible without looking plastic
            if (material.metalness !== undefined) {
              material.metalness = Math.max(material.metalness * 0.5, 0.0);
            }
            
            // Brighten base color by 20% to compensate for simpler lighting
            // This is essential for pants and shoes which are further from main light source
            if (material.color) {
              const brightnessFactor = 1.2; // DO NOT REDUCE - needed for lower clothing visibility
              material.color.r = Math.min(material.color.r * brightnessFactor, 1.0);
              material.color.g = Math.min(material.color.g * brightnessFactor, 1.0);
              material.color.b = Math.min(material.color.b * brightnessFactor, 1.0);
            }
            
            // Keep emissive map but reduce intensity for mobile
            if (material.emissiveMap) {
              material.emissiveIntensity = material.emissiveIntensity ? material.emissiveIntensity * 0.5 : 0.5;
            }
            
            // Ensure transparency works correctly for custom clothes
            if (material.alphaMap || material.transparent) {
              material.transparent = true;
              material.alphaTest = 0.5; // Discard fragments below 50% opacity
              material.depthWrite = true; // Write to depth buffer for proper sorting
            }
          } else {
            // Desktop: enable shadows
            child.castShadow = true;
            child.receiveShadow = true;
          }
          
          material.needsUpdate = true;
        }
      });
      
      if (onLoad) {
        console.log('AvatarModel: Calling onLoad callback');
        onLoad();
      }
    }
  }, [gltf, onLoad, gl]);

  // Return null if URL is invalid (after all hooks have been called)
  if (!url || !url.includes('readyplayer.me')) {
    console.error('AvatarModel: Invalid or missing URL:', url);
    return null;
  }

  return (
    <group ref={meshRef} position={[0, -1.5, 0]}>
      {/* Scale avatar to fit properly in the canvas */}
      <group scale={[1.5, 1.5, 1.5]}>
        <primitive object={gltf.scene} />
      </group>
    </group>
  );
}

// Equipped Item Model Component - loads actual 3D models for items
function EquippedItemModel({ 
  itemId, 
  category, 
  availableItems 
}: { 
  itemId: string;
  category: string;
  availableItems: Array<{
    id: string;
    name: string;
    category: 'hat' | 'shirt' | 'pants' | 'shoes' | 'accessory' | 'background';
    imageUrl: string;
  }>;
}) {
  const item = availableItems.find(i => i.id === itemId);
  if (!item) return null;

  // For now, we'll create placeholder 3D models
  // In a full implementation, you would load actual GLB models from item.imageUrl
  const getItemGeometry = (category: string) => {
    switch (category) {
      case 'hat':
        return <cylinderGeometry args={[0.35, 0.35, 0.25, 8]} />;
      case 'shirt':
        return <boxGeometry args={[0.6, 0.8, 0.15]} />; // Thinner, more clothing-like
      case 'pants':
        return <boxGeometry args={[0.5, 0.6, 0.15]} />; // Thinner, more clothing-like
      case 'shoes':
        return <boxGeometry args={[0.3, 0.2, 0.5]} />; // More shoe-like proportions
      case 'accessory':
        return <sphereGeometry args={[0.15, 8, 8]} />; // Smaller sphere
      default:
        return <boxGeometry args={[0.3, 0.3, 0.3]} />;
    }
  };

  const getItemColor = (category: string) => {
    switch (category) {
      case 'shirt':
        return '#2196F3'; // Blue
      case 'pants':
        return '#4CAF50'; // Green
      case 'shoes':
        return '#FF9800'; // Orange
      case 'hat':
        return '#9C27B0'; // Purple
      case 'accessory':
        return '#F44336'; // Red
      default:
        return '#607D8B'; // Blue Grey
    }
  };

  return (
    <mesh>
      {getItemGeometry(category)}
      <meshBasicMaterial 
        color={getItemColor(category)}
        transparent 
        opacity={category === 'shirt' || category === 'pants' ? 0.6 : 0.8}
      />
    </mesh>
  );
}


// Loading component - use a simple Three.js loading indicator instead of MUI CircularProgress
function LoadingSpinner() {
  return (
    <mesh>
      <sphereGeometry args={[0.5, 16, 16]} />
      <meshBasicMaterial color="#4A90E2" />
    </mesh>
  );
}

// Timeout wrapper for Suspense - Three.js compatible version
function SuspenseWithTimeout({ children, fallback, timeout = 10000 }: { 
  children: React.ReactNode; 
  fallback: React.ReactNode; 
  timeout?: number; 
}) {
  const [hasTimedOut, setHasTimedOut] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isLoadedRef = React.useRef(false);

  React.useEffect(() => {
    console.log('SuspenseWithTimeout: Starting timeout timer for', timeout, 'ms');
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Reset states when children change
    setHasTimedOut(false);
    isLoadedRef.current = false;
    
    timeoutRef.current = setTimeout(() => {
      // Only show timeout if content hasn't loaded yet
      if (!isLoadedRef.current) {
        console.log('SuspenseWithTimeout: Timeout reached, showing timeout indicator');
        setHasTimedOut(true);
      } else {
        console.log('SuspenseWithTimeout: Timeout reached but content already loaded, ignoring');
      }
    }, timeout);

    return () => {
      console.log('SuspenseWithTimeout: Clearing timeout timer');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [timeout, children]); // Include children in dependencies to reset timeout when content changes

  // Track when content loads successfully
  React.useEffect(() => {
    // If we have children and no timeout has occurred, mark as loaded
    if (children && !hasTimedOut) {
      console.log('SuspenseWithTimeout: Content loaded successfully, cancelling timeout');
      isLoadedRef.current = true;
      
      // Clear the timeout since content loaded
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [children, hasTimedOut]);

  if (hasTimedOut) {
    console.log('SuspenseWithTimeout: Rendering timeout indicator (orange sphere)');
    // Return a Three.js compatible loading indicator instead of HTML elements
    return (
      <mesh>
        <sphereGeometry args={[0.8, 16, 16]} />
        <meshBasicMaterial color="#ff9800" />
      </mesh>
    );
  }

  return (
    <React.Suspense fallback={fallback}>
      {children}
    </React.Suspense>
  );
}

// Error component
function ErrorDisplay({ error }: { error: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        p: 2,
      }}
    >
      <Typography variant="body2" color="error" align="center">
        {error}
      </Typography>
    </Box>
  );
}

interface ThreeDAvatarProps {
  avatarUrl: string;
  size?: number;
  backgroundColor?: string;
  isContainerDragging?: boolean;
  onError?: (error: string) => void;
  onAvatarRotatingChange?: (isRotating: boolean) => void;
}

const ThreeDAvatar: React.FC<ThreeDAvatarProps> = ({ 
  avatarUrl, 
  size = 300, 
  backgroundColor = '#f5f5f5', 
  isContainerDragging = false,
  onError,
  onAvatarRotatingChange
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  // Hook to track screen size
  const [screenWidth, setScreenWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [currentSize, setCurrentSize] = React.useState(() => {
    // Try to get saved size from localStorage, fallback to responsive size
    const savedSize = localStorage.getItem('avatarSize');
    if (savedSize) {
      return parseInt(savedSize, 10);
    }
    
    // Responsive default sizing - smaller on mobile to prevent GPU overload
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 600) { // Mobile - keep small to prevent context loss
        return 200;
      } else if (width < 900) { // Tablet
        return 250;
      } else { // Desktop
        return size;
      }
    }
    return size;
  });

  // Update avatar size when screen size changes
  React.useEffect(() => {
    const savedSize = localStorage.getItem('avatarSize');
    
    const getResponsiveDefaultSize = () => {
      if (screenWidth < 600) { // Mobile
        return 250;
      } else if (screenWidth < 900) { // Tablet
        return 300;
      } else { // Desktop
        return size;
      }
    };
    
    if (!savedSize) {
      // No saved size - use responsive default
      const newSize = getResponsiveDefaultSize();
      setCurrentSize(newSize);
    } else {
      // Scale saved size based on screen size
      const saved = parseInt(savedSize, 10);
      const baseSize = size; // Desktop base size
      
      let scale = 1;
      if (screenWidth < 600) { // Mobile
        scale = 250 / baseSize;
      } else if (screenWidth < 900) { // Tablet
        scale = 300 / baseSize;
      }
      
      const newSize = Math.round(saved * scale);
      setCurrentSize(newSize);
      localStorage.setItem('avatarSize', newSize.toString());
    }
  }, [screenWidth, size]);
  const [savedRotation, setSavedRotation] = React.useState(() => {
    // Try to get saved rotation from localStorage
    const saved = localStorage.getItem('avatarRotation');
    return saved ? JSON.parse(saved) : { azimuth: 0, polar: Math.PI / 2 };
  });
  const orbitControlsRef = React.useRef<any>(null);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isResizing, setIsResizing] = React.useState(false);
  const [resizeStart, setResizeStart] = React.useState<{ x: number; y: number; size: number } | null>(null);
  const [isAvatarRotating, setIsAvatarRotating] = React.useState(false);

  const handleError = (errorMessage: string) => {
    console.error('ThreeDAvatar error:', errorMessage);
    setError(errorMessage);
    setLoading(false);
    onError?.(errorMessage);
  };

  const handleModelLoad = () => {
    console.log('ThreeDAvatar: Model loaded successfully');
    setLoading(false);
    setError(null);
  };

  const handleAvatarRotateStart = () => {
    setIsAvatarRotating(true);
    onAvatarRotatingChange?.(true);
  };

  const handleAvatarRotateEnd = () => {
    setIsAvatarRotating(false);
    onAvatarRotatingChange?.(false);
  };

  const handleRotationChange = () => {
    if (orbitControlsRef.current && isInitialized) {
      const rotation = {
        azimuth: orbitControlsRef.current.getAzimuthalAngle(),
        polar: orbitControlsRef.current.getPolarAngle()
      };
      setSavedRotation(rotation);
      localStorage.setItem('avatarRotation', JSON.stringify(rotation));
    }
  };

  // Memoize the canvas key to prevent unnecessary re-renders
  // Don't include isContainerDragging in the key to prevent reloading when dragging
  const canvasKey = useMemo(() => `avatar-${avatarUrl}-${currentSize}`, [avatarUrl, currentSize]);

  // Memoize the AvatarModel to prevent re-renders during dragging
  const AvatarModelMemo = useMemo(() => (
    <AvatarModel url={avatarUrl} onLoad={handleModelLoad} />
  ), [avatarUrl]); // Only re-create when avatarUrl changes, not when dragging

  // Detect mobile for lighting optimization
  const isMobile = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  // Memoize the entire Canvas content to prevent re-renders during dragging
  const CanvasContent = useMemo(() => (
    <>
      {/* 
        ⚠️ CRITICAL: Mobile Avatar Lighting Configuration
        
        DO NOT MODIFY THESE LIGHTING VALUES WITHOUT TESTING ON MOBILE!
        
        Issue: Custom Ready Player Me clothes (especially pants and shoes) appear very dark on mobile
        with simplified lighting. Standard RPM clothes work fine, but custom clothes require specific
        lighting setup to display their colors correctly.
        
        Root Cause: 
        - Mobile uses simplified lighting for performance (no Environment component, no complex shadows)
        - Lower body parts (pants, shoes) are in shadow with standard top-down lighting
        - Custom clothes often have higher roughness/metalness values that appear darker with poor lighting
        
        Solution:
        - Multi-directional lighting with specific fill light from below for lower clothing
        - Enhanced ambient light to ensure base visibility
        - Material property adjustments (see material configuration below)
        
        Verified Working: October 2025 - Tested on iOS/Android with custom clothes
      */}
      {isMobile ? (
        // Mobile: Enhanced lighting to illuminate all parts of avatar (especially lower clothing)
        <>
          {/* Strong ambient light to ensure all areas are visible */}
          <ambientLight intensity={2.5} />
          
          {/* Main directional light from above-front */}
          <directionalLight position={[0, 5, 5]} intensity={1.8} />
          
          {/* CRITICAL: Fill light from below to illuminate pants and shoes - DO NOT REMOVE */}
          <directionalLight position={[0, -3, 3]} intensity={1.2} />
          
          {/* Side rim light for definition */}
          <directionalLight position={[5, 0, 2]} intensity={0.8} />
        </>
      ) : (
        // Desktop: Standard PBR lighting
        <>
          <hemisphereLight args={[0xffffff, 0x444444, 0.6]} />
          <ambientLight intensity={0.4} />
          <directionalLight position={[10, 10, 5]} intensity={1.0} />
          <pointLight position={[-10, -10, -5]} intensity={0.5} />
        </>
      )}
      
      {/* Environment - Only on desktop */}
      {!isMobile && <Environment preset="studio" />}
      
      {/* 3D Model */}
      {AvatarModelMemo}
      
      {/* Interactive Controls - Click and drag to rotate avatar horizontally only */}
      <OrbitControls 
        ref={orbitControlsRef}
        enableZoom={false} 
        enablePan={false}
        enableRotate={true}
        autoRotate={false}
        minPolarAngle={Math.PI / 2} // Restrict vertical rotation - only allow horizontal
        maxPolarAngle={Math.PI / 2} // Keep camera at eye level
        enableDamping={true}
        dampingFactor={0.05}
        target={[0, 0, 0]} // Focus on center of scene
        onStart={handleAvatarRotateStart}
        onEnd={() => {
          handleAvatarRotateEnd();
          handleRotationChange();
        }}
      />
    </>
  ), [avatarUrl, currentSize, isMobile]); // Only re-create when avatarUrl, currentSize, or isMobile changes

  // Reset states when avatarUrl changes
  React.useEffect(() => {
    setError(null);
    setLoading(true);
    console.log('ThreeDAvatar: Loading new avatar URL:', avatarUrl);
  }, [avatarUrl]);

  // Debug: Log when component re-renders
  React.useEffect(() => {
    console.log('ThreeDAvatar: Component rendered with props:', {
      avatarUrl,
      size,
      isContainerDragging,
      currentSize,
      loading,
      error
    });
  });

  // Update currentSize when size prop changes, but only if no saved size exists
  React.useEffect(() => {
    const savedSize = localStorage.getItem('avatarSize');
    if (!savedSize) {
      setCurrentSize(size);
    }
  }, [size]);

  // Set the rotation when component mounts (only once)
  React.useEffect(() => {
    if (orbitControlsRef.current && !isInitialized) {
      orbitControlsRef.current.setAzimuthalAngle(savedRotation.azimuth);
      orbitControlsRef.current.setPolarAngle(savedRotation.polar);
      orbitControlsRef.current.update();
      setIsInitialized(true);
    }
  }, [savedRotation, isInitialized]);

  // Helper function to get client coordinates from mouse or touch event
  const getClientCoordinates = (event: MouseEvent | TouchEvent) => {
    if ('touches' in event) {
      return {
        x: event.touches[0]?.clientX || 0,
        y: event.touches[0]?.clientY || 0
      };
    } else {
      return {
        x: event.clientX,
        y: event.clientY
      };
    }
  };

  const handleResizeStart = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const coords = getClientCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
    setIsResizing(true);
    setResizeStart({
      x: coords.x,
      y: coords.y,
      size: currentSize
    });
  };

  const handleResizeMove = (event: MouseEvent | TouchEvent) => {
    if (!isResizing || !resizeStart) return;

    event.preventDefault();
    
    const coords = getClientCoordinates(event);
    const deltaX = coords.x - resizeStart.x;
    const deltaY = coords.y - resizeStart.y;
    const delta = Math.max(deltaX, deltaY); // Use the larger delta for proportional resizing
    
    // Responsive size constraints
    const getSizeConstraints = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < 600) { // Mobile
          return { min: 150, max: 400 };
        } else if (width < 900) { // Tablet
          return { min: 200, max: 600 };
        } else { // Desktop
          return { min: 200, max: 800 };
        }
      }
      return { min: 200, max: 800 };
    };
    
    const constraints = getSizeConstraints();
    const newSize = Math.max(constraints.min, Math.min(constraints.max, resizeStart.size + delta));
    setCurrentSize(newSize);
    
    // Save the new size to localStorage
    localStorage.setItem('avatarSize', newSize.toString());
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    setResizeStart(null);
  };

  // Function to reset avatar size to default
  const resetAvatarSize = () => {
    setCurrentSize(size);
    localStorage.removeItem('avatarSize');
  };


  React.useEffect(() => {
    if (isResizing) {
      // Add both mouse and touch event listeners
      document.addEventListener('mousemove', handleResizeMove as EventListener);
      document.addEventListener('mouseup', handleResizeEnd);
      document.addEventListener('touchmove', handleResizeMove as EventListener, { passive: false });
      document.addEventListener('touchend', handleResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleResizeMove as EventListener);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.removeEventListener('touchmove', handleResizeMove as EventListener);
        document.removeEventListener('touchend', handleResizeEnd);
      };
    }
  }, [isResizing, resizeStart]);

  return (
             <Box
               sx={{
                 width: currentSize,
                 height: currentSize,
                 display: 'flex',
                 justifyContent: 'center',
                 alignItems: 'center',
                 cursor: 'grab',
                 '&:active': {
                   cursor: 'grabbing',
                 },
                 borderRadius: 2,
                 overflow: 'hidden',
                 border: 'none',
                 boxShadow: 'none',
                 position: 'relative',
                 backgroundColor: 'transparent',
                 backdropFilter: 'none',
                 '&:hover': {
                   boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                   '& .resize-handle': {
                     opacity: 1,
                   }
                 }
               }}
               title="Click and drag to rotate avatar"
             >
      {error ? (
        <ErrorDisplay error={error} />
      ) : (
        <ModelErrorBoundary onError={handleError}>
                <Canvas 
                  key={canvasKey}
                  camera={{ position: [0, 0, 4], fov: 50 }}
            gl={{ 
              antialias: false, // Disable antialiasing to reduce GPU load
              alpha: true,
              premultipliedAlpha: false, // CRITICAL: Avoids black halos/darkening on mobile (especially iOS)
              powerPreference: "low-power", // Use low-power mode to prevent context loss
              outputColorSpace: THREE.SRGBColorSpace, // Correct for THREE r152+ (not string 'srgb')
              toneMapping: THREE.ACESFilmicToneMapping,
              // Prevent context loss by being conservative with resources
              preserveDrawingBuffer: false, // Don't preserve buffer (saves memory)
              failIfMajorPerformanceCaveat: false, // Don't fail on slower devices
              depth: true, // Enable depth buffer
              stencil: false, // Disable stencil buffer (saves memory)
            }}
                       onCreated={({ gl }) => {
                         gl.setClearColor(0x000000, 0); // Transparent background
                         
                         // Configure renderer for proper color space (THREE r152+)
                         gl.outputColorSpace = THREE.SRGBColorSpace; // Use constant, not string
                         gl.toneMapping = THREE.ACESFilmicToneMapping; // Keep tone mapping for proper PBR colors
                         gl.toneMappingExposure = 1.0;
                         
                         // Aggressive optimizations to prevent context loss
                         gl.setPixelRatio(Math.min(window.devicePixelRatio, 1)); // Limit to 1x pixel ratio to save GPU memory
                         gl.shadowMap.enabled = false; // Disable shadows completely
                         
                         // Handle WebGL context loss using the renderer's DOM element
                         const canvas = gl.domElement;
                         if (canvas) {
                           canvas.addEventListener('webglcontextlost', (event: Event) => {
                             console.warn('WebGL context lost');
                             event.preventDefault();
                             setError('WebGL context lost. Please refresh the page.');
                           }, false);
                           
                           canvas.addEventListener('webglcontextrestored', () => {
                             console.log('WebGL context restored');
                             setError(null);
                             setLoading(true);
                           }, false);
                         }
                       }}
          >
            <SuspenseWithTimeout fallback={<LoadingSpinner />} timeout={10000}>
              {CanvasContent}
            </SuspenseWithTimeout>
          </Canvas>
        </ModelErrorBoundary>
      )}
      
       {/* Resize Handle */}
       <Box
         className="resize-handle"
         onMouseDown={handleResizeStart}
         onTouchStart={handleResizeStart}
         sx={{
           position: 'absolute',
           bottom: 0,
           right: 0,
           width: { xs: 28, sm: 24, md: 20 }, // Larger on mobile
           height: { xs: 28, sm: 24, md: 20 }, // Larger on mobile
           cursor: 'nw-resize',
           opacity: 0,
           background: 'linear-gradient(-45deg, transparent 30%, #2196F3 30%, #2196F3 40%, transparent 40%, transparent 60%, #2196F3 60%, #2196F3 70%, transparent 70%)',
           borderBottomRightRadius: 2,
           transition: 'opacity 0.2s ease',
           '&:hover': {
             background: 'linear-gradient(-45deg, transparent 30%, #1976D2 30%, #1976D2 40%, transparent 40%, transparent 60%, #1976D2 60%, #1976D2 70%, transparent 70%)',
           },
           '&:active': {
             cursor: 'nw-resize',
           },
           // Make it more visible on touch devices
           '@media (hover: none)': {
             opacity: 0.3, // Show on touch devices
           }
         }}
         title="Drag to resize avatar"
       />
    </Box>
  );
};

export default ThreeDAvatar;